//libraries
import React, { Suspense } from 'react';
import styled from 'styled-components';
import { HashLink } from 'react-router-hash-link';

// styles
import { Frame, PrimaryButton, Flex, ColumnedFlex, CentredFlex } from 'shared/styledComponents';
import { H2, H3, H1 } from 'shared/typography';

// theme
import devices from 'theme/devices';

// utils
import parseHtml from 'utils/parseHtmlToReact';

// custom components
import Layout from 'components/Layout';
import ParentbirthContentAd from 'components/ParentbirthContentAd';

const RelatedArticles = React.lazy(() => import('./RelatedArticles'));
const ArticleAuthorName = React.lazy(() => import('./ArticleAuthorName'));
const ServicesBlock = React.lazy(() => import('./ServicesBlock'));

const MainSection = styled(ColumnedFlex)<any>`
  width: 100%;
  align-items: center;
  background-color: ${({ theme: { colors } }): string => colors.secondary};
  padding-bottom: 150px;
`;

const MainHeading = styled(H1)`
  font-size: 74px;
  margin-bottom: 25px;
  text-align: center;
`;

const ImageWrapper = styled(CentredFlex)`
  width: 100%;
  margin-top: -95px;
`;

const Image = styled.div<any>`
  width: 945px;
  height: 630px;
  background-image: ${({ src }): string => `url(${src})`};
  background-position: center;
  background-size: cover;
  background-position-y: 0;
  background-repeat: no-repeat;
  cursor: pointer;
  margin-bottom: 105px;

  @media ${devices.mobile} {
    width: 100%;
    height: 335px;
    margin-bottom: 30px;
  }
`;

const BodySection = styled(ColumnedFlex)`
  align-items: center;
  margin-bottom: 100px;

  @media ${devices.mobile} {
    margin-bottom: 80px;
  }
`;

const Body = styled(ColumnedFlex)`
  align-items: center;
  max-width: 730px;
  word-break: break-word;
`;

// const ShareBlock = styled(ColumnedFlex)``;

// const ShareHeading = styled(Eyebrow)`
//   margin-bottom: 20px;
// `;

// const SocialIcons = styled(Flex)`
//   width: 100%;
//   max-width: 100px;
//   justify-content: space-between;
// `;

// const SocialIcon = styled(Icon)<any>``;

export const RelatedArticlesSection = styled(ColumnedFlex)<any>`
  width: 100%;
  align-items: flex-start;
  margin-bottom: 150px;

  @media ${devices.mobile} {
    margin-bottom: 130px;
  }
`;

export const RelatedArticlesHeading = styled(H3)`
  margin-bottom: 35px;

  @media ${devices.mobile} {
    margin-bottom: 25px;
  }
`;

const ContactSection = styled(Flex)<any>`
  justify-content: center;
  background-color: ${({ theme: { colors } }): string => colors.secondary};
  padding: 100px 0;

  @media ${devices.mobile} {
    padding: 50px 0;
  }
`;

const ContactSectionContent = styled(ColumnedFlex)<any>`
  align-items: center;
  max-width: 480px;

  @media ${devices.mobile} {
    max-width: 210px;
  }
`;

const ContactSectionHeading = styled(H2)<any>`
  text-align: center;
  margin-bottom: 60px;
`;

const ContactSectionButtons = styled(Flex)<any>`
  @media ${devices.mobile} {
    flex-direction: column;
  }
`;

const JoinCommunityButton = styled(PrimaryButton)(
  ({ theme: { colors } }) => `
    padding: 15px 16px;
    margin-left: 25px;
    background-color: ${colors.transparent};
    border: 2px solid ${colors.primary};
    color:${colors.primary};
    
    @media ${devices.mobile} {
      margin-left: 0;
      margin-top: 15px;
    }
  `,
);

const Article = ({
  articleInfo: { title, providerName, providerSlug, author, credentials, services, photoUrl, body },
  articles,
  onRelatedArticleClick,
  onShareStoryButtonClick,
}: any): JSX.Element => (
  <Layout>
    <MainSection as="section">
      <Frame>
        <Suspense fallback={<div>Loading...</div>}>
          <ServicesBlock services={services} />
        </Suspense>
        <MainHeading>{title}</MainHeading>
        {(providerName || author) && (
          <Suspense fallback={<div>Loading...</div>}>
            <ArticleAuthorName
              providerName={providerName}
              providerSlug={providerSlug}
              author={author}
              credentials={credentials}
            />
          </Suspense>
        )}
      </Frame>
    </MainSection>
    <Frame>
      <ImageWrapper>
        <Image src={photoUrl} />
      </ImageWrapper>
      <BodySection as="section">
        <ParentbirthContentAd />
        <Body>{parseHtml(body)}</Body>
        {/* <ShareBlock>
              <ShareHeading>Share</ShareHeading>
              <SocialIcons>
                <SocialIcon type="facebook" />
                <SocialIcon type="twitter" />
                <SocialIcon type="mail" />
              </SocialIcons>
            </ShareBlock> */}
      </BodySection>
      {articles && !!articles.length && (
        <RelatedArticlesSection as="section">
          <RelatedArticlesHeading>Related Articles</RelatedArticlesHeading>
          <Suspense fallback={<div>Loading...</div>}>
            <RelatedArticles articles={articles} onRelatedArticleClick={onRelatedArticleClick} />
          </Suspense>
        </RelatedArticlesSection>
      )}
      <ContactSection as="section">
        <ContactSectionContent>
          <ContactSectionHeading>Help us change the conversation.</ContactSectionHeading>
          <ContactSectionButtons>
            <PrimaryButton size="small" onClick={onShareStoryButtonClick}>
              share your story
            </PrimaryButton>
            <JoinCommunityButton as={HashLink} to="#listYourPractice" size="small" smooth>
              join our community
            </JoinCommunityButton>
          </ContactSectionButtons>
        </ContactSectionContent>
      </ContactSection>
    </Frame>
  </Layout>
);

export default Article;
